import type { Locale } from '@/types/content'

export const locales: Locale[] = ['en', 'hi']

export const defaultLocale: Locale = 'en'

export const localeLabels: Record<Locale, string> = {
    en: 'English',
    hi: 'हिन्दी'
}

export const ui = {
    en: {
        siteName: "Padma Purana",
        siteTagline: "The Jain Ramayana of Acharya Ravishena",
        siteDescription: "Explore the Jain Padma Purana in English and Hindi: an illustrated family tree of the Ikshvaku, Vidyadhara, Rakshasa, Vanara and Hari clans, the story in twelve parts, and all 123 chapters in detail.",
        navHome: "Home",
        navLineage: "Family Tree",
        navStory: "The Story",
        navChapters: "123 Chapters",
        switchTo: "हिन्दी",
        switchLabel: "Read in Hindi",
        skipToContent: "Skip to content",
        openMenu: "Open menu",
        closeMenu: "Close menu",
        footerNote: "Summaries are drawn from the Hindi Padma Purana (Paras Prakashan, 2009), the Hindi rendering by Aryika Dakshmati Mataji of Pandit Daulatram's Dhundhari vachanika of Acharya Ravishena's Padmacharita.",
        footerExplore: "Explore",
        footerAbout: "About the text",
        footerAboutBody: "Acharya Ravishena composed the Padmacharita in Sanskrit in the seventh century. It tells the life of Padma, that is Rama, the eighth Baladeva, within Jain cosmology and ethics.",
        parva: "Parva",
        chapter: "Chapter",
        chapters: "Chapters",
        readChapter: "Read chapter",
        backToChapters: "All chapters",
        previous: "Previous",
        next: "Next",
        keyPoints: "What happens",
        people: "People in this chapter",
        places: "Places",
        inArc: "Part of",
        searchChapters: "Search chapters, people or places",
        noResults: "Nothing matches that search.",
        exploreTree: "Explore the family tree",
        readStory: "Read the story",
        browseChapters: "Browse all 123 chapters",
        lineageTitle: "Family Trees",
        storyTitle: "The Story",
        chaptersTitle: "All 123 Chapters",
        clans: "Clans",
        founder: "Founder",
        seat: "Seat",
        emblem: "Emblem",
        parents: "Parents",
        spouses: "Spouses",
        children: "Children",
        siblings: "Siblings",
        appearsIn: "Appears in",
        fate: "Final destiny",
        kings: "kings",
        points: "points",
        searchPeople: "Search a name in English or Hindi",
        resetView: "Reset view",
        showAll: "All clans",
        listView: "Browse as a list",
        selectHint: "Tap any card to read about that person.",
        close: "Close",
        generation: "Generation",
        era: "Era",
        chaptersIntro: "Acharya Ravishena tells the life of Padma, that is Rama, in 123 parvas. Each one is summarised here with its key moments, the people who appear in it and the places where it unfolds.",
        statArcs: "Story arcs",
        statMoments: "Key moments",
        arcNumber: "Arc {number}",
        parvaRange: "Parvas {from} to {to}",
        filterAll: "All",
        filterByArc: "Filter by story arc",
        clearSearch: "Clear search",
        clearFilters: "Clear filters",
        chaptersShowingAll: "Showing all {total} chapters",
        chaptersShowing: "Showing {shown} of {total} chapters",
        searchMatches: "Matches",
        chapterSummary: "Summary",
        breadcrumbLabel: "Breadcrumb",
        chapterNavLabel: "Previous and next chapters",
        peopleHint: "Tap a name to find them in the family tree.",
        storyDescription: "The Jain Ramayana of Acharya Ravishena retold in twelve movements, from the first dynasties and the rise of Ravana to Rama's liberation, with the key events of each and links to every chapter.",
        storyJumpLabel: "Jump to an arc",
        storyKeyEvents: "Key events",
        storyMainPoints: "Main points",
        storyChaptersInPart: "Chapters in this part",
        storyStartReading: "Start reading at Parva {number}",
        storyPending: "Summaries of the chapters in this part are on their way.",
        storyBackToArcs: "Back to all arcs"
    },
    hi: {
        siteName: "पद्मपुराण",
        siteTagline: "आचार्य रविषेण की जैन रामायण",
        siteDescription: "जैन पद्मपुराण को हिन्दी और अंग्रेज़ी में जानिए: इक्ष्वाकु, विद्याधर, राक्षस, वानर और हरि वंशों का सचित्र वंशवृक्ष, बारह खंडों में कथा और सभी 123 पर्व विस्तार से।",
        navHome: "मुखपृष्ठ",
        navLineage: "वंशवृक्ष",
        navStory: "कथा",
        navChapters: "123 पर्व",
        switchTo: "English",
        switchLabel: "Read in English",
        skipToContent: "मुख्य सामग्री पर जाएँ",
        openMenu: "मेनू खोलें",
        closeMenu: "मेनू बंद करें",
        footerNote: "सारांश हिन्दी पद्मपुराण (पारस प्रकाशन, 2009) पर आधारित हैं, जो आचार्य रविषेण के पद्मचरित की पण्डित दौलतराम जी कृत ढूँढारी वचनिका का आर्यिका दक्षमती माताजी द्वारा किया गया हिन्दी रूपान्तर है।",
        footerExplore: "देखें",
        footerAbout: "ग्रंथ परिचय",
        footerAboutBody: "आचार्य रविषेण ने सातवीं शताब्दी में संस्कृत में पद्मचरित की रचना की। इसमें आठवें बलभद्र पद्म अर्थात राम का जीवन जैन लोक-दर्शन और आचार के प्रकाश में वर्णित है।",
        parva: "पर्व",
        chapter: "पर्व",
        chapters: "पर्व",
        readChapter: "पर्व पढ़ें",
        backToChapters: "सभी पर्व",
        previous: "पिछला",
        next: "अगला",
        keyPoints: "क्या हुआ",
        people: "इस पर्व के पात्र",
        places: "स्थान",
        inArc: "कथा खंड",
        searchChapters: "पर्व, पात्र या स्थान खोजें",
        noResults: "इस खोज से कुछ नहीं मिला।",
        exploreTree: "वंशवृक्ष देखें",
        readStory: "कथा पढ़ें",
        browseChapters: "सभी 123 पर्व देखें",
        lineageTitle: "वंशवृक्ष",
        storyTitle: "कथा",
        chaptersTitle: "सभी 123 पर्व",
        clans: "वंश",
        founder: "संस्थापक",
        seat: "राजधानी",
        emblem: "चिह्न",
        parents: "माता-पिता",
        spouses: "जीवनसाथी",
        children: "संतान",
        siblings: "भाई-बहन",
        appearsIn: "इन पर्वों में",
        fate: "अंतिम गति",
        kings: "राजा",
        points: "बिंदु",
        searchPeople: "हिन्दी या अंग्रेज़ी में नाम खोजें",
        resetView: "दृश्य रीसेट करें",
        showAll: "सभी वंश",
        listView: "सूची के रूप में देखें",
        selectHint: "किसी भी कार्ड को छूकर उस व्यक्ति के बारे में पढ़ें।",
        close: "बंद करें",
        generation: "पीढ़ी",
        era: "काल",
        chaptersIntro: "आचार्य रविषेण ने पद्म अर्थात राम का जीवन 123 पर्वों में कहा है। यहाँ हर पर्व का सारांश, उसके मुख्य प्रसंग, उसमें आने वाले पात्र और वे स्थान दिए गए हैं जहाँ कथा घटित होती है।",
        statArcs: "कथा खंड",
        statMoments: "मुख्य प्रसंग",
        arcNumber: "खंड {number}",
        parvaRange: "पर्व {from} से {to}",
        filterAll: "सभी",
        filterByArc: "कथा खंड के अनुसार छाँटें",
        clearSearch: "खोज साफ़ करें",
        clearFilters: "फ़िल्टर हटाएँ",
        chaptersShowingAll: "सभी {total} पर्व दिख रहे हैं",
        chaptersShowing: "{total} में से {shown} पर्व दिख रहे हैं",
        searchMatches: "मेल खाते नाम",
        chapterSummary: "सारांश",
        breadcrumbLabel: "नेविगेशन पथ",
        chapterNavLabel: "पिछला और अगला पर्व",
        peopleHint: "किसी नाम को छूकर उसे वंशवृक्ष में देखें।",
        storyDescription: "आचार्य रविषेण की जैन रामायण बारह खंडों में, पहले वंशों और रावण के उदय से राम के निर्वाण तक, हर खंड के मुख्य प्रसंगों और उसके पर्वों की कड़ियों के साथ।",
        storyJumpLabel: "कथा खंड पर जाएँ",
        storyKeyEvents: "मुख्य घटनाएँ",
        storyMainPoints: "मुख्य बिंदु",
        storyChaptersInPart: "इस खंड के पर्व",
        storyStartReading: "पर्व {number} से पढ़ना आरम्भ करें",
        storyPending: "इस खंड के पर्वों के सारांश शीघ्र जोड़े जाएँगे।",
        storyBackToArcs: "सभी कथा खंडों पर लौटें"
    }
} as const

export type UiStrings = { [K in keyof typeof ui.en]: string }
